import React, { lazy, Suspense, useContext } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from './context/ThemeContext';
import { AuthContext, AuthProvider } from './context/AuthContext';
import { PlatformSettingsContext, PlatformSettingsProvider } from './context/PlatformSettingsContext';
import { CreditsProvider } from './context/CreditsContext';
import { SupportUnreadProvider } from './context/SupportUnreadContext';
import { ComplianceProvider } from './context/ComplianceContext';
import { getDefaultRoute } from './utils/navVisibility';
import { isCurator } from './utils/roles';
import LandingPage from './pages/LandingPage';

import ScrollToTop from './components/ScrollToTop';
import CookieNotice from './components/CookieNotice';
import EmailGate from './components/auth/EmailGate';

// Each page is its own chunk. A first visit downloads the page it asked
// for rather than the whole product — before this, the landing page pulled
// in the admin screens, the product wizard and the tank manager before it
// could paint a single pixel.
const AboutPage = lazy(() => import('./pages/AboutPage'));
const AdminCatalogCategoriesPage = lazy(() => import('./pages/AdminCatalogCategoriesPage'));
const AdminCatalogProductDetailPage = lazy(() => import('./pages/AdminCatalogProductDetailPage'));
const AdminCouponsPage = lazy(() => import('./pages/AdminCouponsPage'));
const AdminDashboardPage = lazy(() => import('./pages/AdminDashboardPage'));
const AdminMetadataPage = lazy(() => import('./pages/AdminMetadataPage'));
const AdminRevenuePage = lazy(() => import('./pages/AdminRevenuePage'));
const AdminSettingsPage = lazy(() => import('./pages/AdminSettingsPage'));
const AdminSupportPage = lazy(() => import('./pages/AdminSupportPage'));
const AdminTemplatesPage = lazy(() => import('./pages/AdminTemplatesPage'));
const AdminTransactionsPage = lazy(() => import('./pages/AdminTransactionsPage'));
const AdminUserDetailPage = lazy(() => import('./pages/AdminUserDetailPage'));
const AdminUsersPage = lazy(() => import('./pages/AdminUsersPage'));
const AdminVendorDetailPage = lazy(() => import('./pages/AdminVendorDetailPage'));
const AdminVendorsPage = lazy(() => import('./pages/AdminVendorsPage'));
const BulkListingPage = lazy(() => import('./pages/BulkListingPage'));
const CatalogComparePage = lazy(() => import('./pages/CatalogComparePage'));
const CatalogCurationPage = lazy(() => import('./pages/CatalogCurationPage'));
const CatalogPage = lazy(() => import('./pages/CatalogPage'));
const CatalogProductPage = lazy(() => import('./pages/CatalogProductPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const CookiePolicyPage = lazy(() => import('./pages/CookiePolicyPage'));
const DemoPage = lazy(() => import('./pages/DemoPage'));
const DisclaimerPage = lazy(() => import('./pages/DisclaimerPage'));
const GenerationRunPage = lazy(() => import('./pages/GenerationRunPage'));
const GenerationRunsPage = lazy(() => import('./pages/GenerationRunsPage'));
const GoLivePage = lazy(() => import('./pages/GoLivePage'));
const InventoryPage = lazy(() => import('./pages/InventoryPage'));
const LabelCropperPage = lazy(() => import('./pages/LabelCropperPage'));
const ListingAssistantPage = lazy(() => import('./pages/ListingAssistantPage'));
const ListingsPage = lazy(() => import('./pages/ListingsPage'));
const LiveListingPage = lazy(() => import('./pages/LiveListingPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const OrdersPage = lazy(() => import('./pages/OrdersPage'));
const PlanPage = lazy(() => import('./pages/PlanPage'));
const PricingPage = lazy(() => import('./pages/PricingPage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const ProductDetailPage = lazy(() => import('./pages/ProductDetailPage'));
const ProductWizardPage = lazy(() => import('./pages/ProductWizardPage'));
const ProductsPage = lazy(() => import('./pages/ProductsPage'));
const PublishQueuePage = lazy(() => import('./pages/PublishQueuePage'));
const RefundPolicyPage = lazy(() => import('./pages/RefundPolicyPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const ReportsPage = lazy(() => import('./pages/ReportsPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const SellerDashboardPage = lazy(() => import('./pages/SellerDashboardPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const SupportCenterPage = lazy(() => import('./pages/SupportCenterPage'));
const SupportPage = lazy(() => import('./pages/SupportPage'));
const TankManagerPage = lazy(() => import('./pages/TankManagerPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const ToolsPage = lazy(() => import('./pages/ToolsPage'));
const TransactionsPage = lazy(() => import('./pages/TransactionsPage'));
const UploadQueuePage = lazy(() => import('./pages/UploadQueuePage'));
const VendorDashboardPage = lazy(() => import('./pages/VendorDashboardPage'));
const VendorProductFormPage = lazy(() => import('./pages/VendorProductFormPage'));
const VendorProductsPage = lazy(() => import('./pages/VendorProductsPage'));
const VendorProfilePage = lazy(() => import('./pages/VendorProfilePage'));
const VendorPublicProfilePage = lazy(() => import('./pages/VendorPublicProfilePage'));
const VendorRegisterPage = lazy(() => import('./pages/VendorRegisterPage'));
const VendorReportsPage = lazy(() => import('./pages/VendorReportsPage'));

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useContext(AuthContext);

  if (loading) {
    return <div className="flex items-center justify-center h-screen">Loading...</div>;
  }

  // Every protected page passes through the gate, so an account whose address
  // has not been proven cannot reach any of them. The gate itself opens if it
  // cannot reach the server — a verification feature that locks everybody out
  // during an outage has done more damage than the problem it solves.
  return isAuthenticated ? <EmailGate>{children}</EmailGate> : <Navigate to="/login" />;
};

// Admin-only pages (Admin Dashboard, User Management, Nav Visibility
// settings) — a non-admin who somehow lands here (typed URL, bookmark) is
// bounced to their own default landing page, not shown an error page.
const AdminRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useContext(AuthContext);
  const { navVisibility } = useContext(PlatformSettingsContext);

  if (loading) {
    return <div className="flex items-center justify-center h-screen">Loading...</div>;
  }
  if (!isAuthenticated) return <Navigate to="/login" />;
  if (user?.role !== 'admin') return <Navigate to={getDefaultRoute(navVisibility, false)} />;

  return children;
};

// Curation pages — admin AND prime seller, the same pair the backend gates
// catalog curation on. Not AdminRoute: a prime seller is not an admin, but
// reviewing raw vendor uploads is precisely their job.
const CuratorRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useContext(AuthContext);
  const { navVisibility } = useContext(PlatformSettingsContext);

  if (loading) {
    return <div className="flex items-center justify-center h-screen">Loading...</div>;
  }
  if (!isAuthenticated) return <Navigate to="/login" />;
  if (!isCurator(user)) return <Navigate to={getDefaultRoute(navVisibility, false)} replace />;

  return children;
};

// Vendor-only pages. A vendor whose application is still pending is NOT
// bounced out — their dashboard/profile are exactly where they see their
// approval state — the backend is what refuses the actions they can't take
// yet (see middleware/requireVendor.js).
const VendorRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useContext(AuthContext);
  const { navVisibility } = useContext(PlatformSettingsContext);

  if (loading) {
    return <div className="flex items-center justify-center h-screen">Loading...</div>;
  }
  if (!isAuthenticated) return <Navigate to="/login" />;
  if (user?.role !== 'vendor') return <Navigate to={getDefaultRoute(navVisibility, user?.role === 'admin')} />;

  return children;
};

// Gates one of the seven admin-toggleable sections. Hiding a section from
// the nav (see Header.jsx) isn't real enforcement on its own — a seller
// could still type the URL directly, so every toggleable route is wrapped
// in this too. Admins always pass through regardless of the toggle, since
// hiding a section from the person managing that toggle would be
// self-defeating.
const FeatureGate = ({ featureKey, children }) => {
  const { user } = useContext(AuthContext);
  const { navVisibility, loading } = useContext(PlatformSettingsContext);

  if (loading) {
    return <div className="flex items-center justify-center h-screen">Loading...</div>;
  }

  // Vendors have no business on seller pages at all, regardless of which
  // sections the admin has left visible — send them to their own area.
  if (user?.role === 'vendor') return <Navigate to="/vendor" replace />;

  const isAdmin = user?.role === 'admin';
  const visible = isAdmin || navVisibility?.[featureKey] !== false;
  if (!visible) return <Navigate to={getDefaultRoute(navVisibility, isAdmin)} replace />;

  return children;
};

// A page that only makes sense for someone who sells. An admin landing on one
// is redirected somewhere that does the same job for them, rather than being
// shown a seller's version of a thing they run.
const SellerOnly = ({ to, children }) => {
  const { user } = useContext(AuthContext);
  return user?.role === 'admin' ? <Navigate to={to} replace /> : children;
};

// Logged out: the public landing page lives here, not a redirect to /login —
// a visitor should be able to see what this is before being asked to sign
// in. Logged in: straight to wherever that user's own dashboard is.
const RootRedirect = () => {
  const { user, isAuthenticated, loading } = useContext(AuthContext);
  const { navVisibility } = useContext(PlatformSettingsContext);

  if (loading) {
    return <div className="flex items-center justify-center h-screen">Loading...</div>;
  }
  if (!isAuthenticated) return <LandingPage />;
  if (user?.role === 'vendor') return <Navigate to="/vendor" />;
  return <Navigate to={getDefaultRoute(navVisibility, user?.role === 'admin')} />;
};

const AppRoutes = () => {
  // Every page below is its own chunk, so a first visit downloads the page
  // asked for rather than the whole product — the landing page was paying for
  // the admin screens, the product wizard and the tank manager before it could
  // paint anything. The landing page itself stays eager: making it a separate
  // chunk would add a round trip to the very render being optimised.
  return (
    <Suspense fallback={<RouteLoading />}>
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/cookies" element={<CookiePolicyPage />} />
      <Route path="/refunds" element={<RefundPolicyPage />} />
      <Route path="/disclaimer" element={<DisclaimerPage />} />
      <Route path="/demo" element={<DemoPage />} />
      <Route path="/vendor/register" element={<VendorRegisterPage />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <FeatureGate featureKey="dashboard">
              <SellerDashboardPage />
            </FeatureGate>
          </ProtectedRoute>
        }
      />
      <Route
        path="/products"
        element={
          <ProtectedRoute>
            <FeatureGate featureKey="products">
              <ProductsPage />
            </FeatureGate>
          </ProtectedRoute>
        }
      />
      <Route
        path="/products/new"
        element={
          <ProtectedRoute>
            <FeatureGate featureKey="products">
              <ProductWizardPage />
            </FeatureGate>
          </ProtectedRoute>
        }
      />
      {/* Public on purpose. Pricing is a sales page: a visitor deciding whether
          to sign up has to be able to read it first. It renders inside the app
          shell when signed in and the marketing shell when not. */}
      <Route path="/pricing" element={<PricingPage />} />
      <Route
        path="/tools/label-cropper"
        element={<ProtectedRoute><LabelCropperPage /></ProtectedRoute>}
      />
      <Route
        path="/tools"
        element={<ProtectedRoute><ToolsPage /></ProtectedRoute>}
      />
      {/* /support is PUBLIC now — a help page anyone can read, including
          someone deciding whether to sign up. The ticket system, which does
          need an account, moved one level down. An admin typing the ticket
          URL is still sent to the desk: they answer tickets, not file them. */}
      <Route path="/support" element={<SupportCenterPage />} />
      <Route
        path="/support/tickets"
        element={<ProtectedRoute><SellerOnly to="/admin/support"><SupportPage /></SellerOnly></ProtectedRoute>}
      />
      <Route
        path="/catalog/curation"
        element={
          <ProtectedRoute>
            <CuratorRoute>
              <CatalogCurationPage />
            </CuratorRoute>
          </ProtectedRoute>
        }
      />
      {/* The product AS A PAGE — what a seller lands on from the catalog once
          they have imported something. Read-only on purpose: every way to
          change a value is a button that goes to the form. */}
      <Route
        path="/products/:id"
        element={
          <ProtectedRoute>
            <FeatureGate featureKey="products">
              <ProductDetailPage />
            </FeatureGate>
          </ProtectedRoute>
        }
      />
      {/* The assistant is the way IN to a listing; the edit form below stays
          exactly as it was, as the advanced path once a listing exists. */}
      <Route
        path="/products/:id/assistant"
        element={
          <ProtectedRoute>
            <ListingAssistantPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/products/:id/edit"
        element={
          <ProtectedRoute>
            <FeatureGate featureKey="products">
              <ProductWizardPage />
            </FeatureGate>
          </ProtectedRoute>
        }
      />
      <Route
        path="/orders"
        element={
          <ProtectedRoute>
            <FeatureGate featureKey="orders">
              <OrdersPage />
            </FeatureGate>
          </ProtectedRoute>
        }
      />
      <Route
        path="/listings"
        element={
          <ProtectedRoute>
            <FeatureGate featureKey="listings">
              <ListingsPage />
            </FeatureGate>
          </ProtectedRoute>
        }
      />
      <Route
        path="/inventory"
        element={
          <ProtectedRoute>
            <FeatureGate featureKey="inventory">
              <InventoryPage />
            </FeatureGate>
          </ProtectedRoute>
        }
      />
      <Route
        path="/catalog"
        element={
          <ProtectedRoute>
            <FeatureGate featureKey="catalog">
              <CatalogPage />
            </FeatureGate>
          </ProtectedRoute>
        }
      />
      <Route
        path="/catalog/compare"
        element={
          <ProtectedRoute>
            <FeatureGate featureKey="catalog">
              <CatalogComparePage />
            </FeatureGate>
          </ProtectedRoute>
        }
      />
      <Route
        path="/catalog/vendor/:id"
        element={
          <ProtectedRoute>
            <FeatureGate featureKey="catalog">
              <VendorPublicProfilePage />
            </FeatureGate>
          </ProtectedRoute>
        }
      />
      <Route
        path="/catalog/:id"
        element={
          <ProtectedRoute>
            <FeatureGate featureKey="catalog">
              <CatalogProductPage />
            </FeatureGate>
          </ProtectedRoute>
        }
      />
      <Route
        path="/listings/generation"
        element={
          <ProtectedRoute>
            <FeatureGate featureKey="listings">
              <GenerationRunsPage />
            </FeatureGate>
          </ProtectedRoute>
        }
      />
      <Route
        path="/listings/generation/:id"
        element={
          <ProtectedRoute>
            <FeatureGate featureKey="listings">
              <GenerationRunPage />
            </FeatureGate>
          </ProtectedRoute>
        }
      />
      {/* The export pages are gone — files are generated from Ready to List
          now. There is no catch-all route in this app, so without this a
          bookmark or an old link would render a blank white page rather than
          anything a seller could act on. */}
      <Route path="/listings/exports/*" element={<Navigate to="/products?tab=ready" replace />} />
      <Route path="/listings/exports" element={<Navigate to="/products?tab=ready" replace />} />
      <Route
        path="/listings/upload-queue"
        element={
          <ProtectedRoute>
            <FeatureGate featureKey="listings">
              <UploadQueuePage />
            </FeatureGate>
          </ProtectedRoute>
        }
      />
      <Route
        path="/products/:id/live-listing"
        element={
          <ProtectedRoute>
            <FeatureGate featureKey="products">
              <LiveListingPage />
            </FeatureGate>
          </ProtectedRoute>
        }
      />
      <Route
        path="/publish-queue"
        element={
          <ProtectedRoute>
            <FeatureGate featureKey="products">
              <PublishQueuePage />
            </FeatureGate>
          </ProtectedRoute>
        }
      />
      <Route
        path="/go-live"
        element={
          <ProtectedRoute>
            <FeatureGate featureKey="products">
              <GoLivePage />
            </FeatureGate>
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports"
        element={
          <ProtectedRoute>
            <FeatureGate featureKey="reports">
              <ReportsPage />
            </FeatureGate>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <SettingsPage />
          </ProtectedRoute>
        }
      />
      {/* Reached from the Transaction history card in settings. Protected,
          not gated by a feature flag: a seller can always see what they paid. */}
      <Route
        path="/transactions"
        element={
          <ProtectedRoute>
            <TransactionsPage />
          </ProtectedRoute>
        }
      />
      {/* Reached by clicking the plan name in the profile. */}
      <Route
        path="/plan"
        element={
          <ProtectedRoute>
            <PlanPage />
          </ProtectedRoute>
        }
      />
      {/* Tank Manager is NOT wrapped in AdminRoute: a prime seller curates
          tanks too, and they are not an admin. The page and the API both
          check for either role — the API being the one that matters. */}
      <Route
        path="/tanks"
        element={
          <ProtectedRoute>
            <TankManagerPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <AdminRoute>
              <AdminDashboardPage />
            </AdminRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/users"
        element={
          <ProtectedRoute>
            <AdminRoute>
              <AdminUsersPage />
            </AdminRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/users/:id"
        element={
          <ProtectedRoute>
            <AdminRoute>
              <AdminUserDetailPage />
            </AdminRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/vendors"
        element={
          <ProtectedRoute>
            <AdminRoute>
              <AdminVendorsPage />
            </AdminRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/vendors/:id"
        element={
          <ProtectedRoute>
            <AdminRoute>
              <AdminVendorDetailPage />
            </AdminRoute>
          </ProtectedRoute>
        }
      />
      {/* CuratorRoute, not AdminRoute. Assigning a marketplace category is the
          prime seller's actual job — the curation queue sends them straight
          here from its "Assign category" button, and under AdminRoute that
          button silently bounced them back to their dashboard with no error
          and nothing done. The API gates these two on requireCurator to match. */}
      <Route
        path="/admin/catalog-categories"
        element={
          <ProtectedRoute>
            <CuratorRoute>
              <AdminCatalogCategoriesPage />
            </CuratorRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/catalog-categories/:id"
        element={
          <ProtectedRoute>
            <CuratorRoute>
              <AdminCatalogProductDetailPage />
            </CuratorRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/templates"
        element={
          <ProtectedRoute>
            <AdminRoute>
              <AdminTemplatesPage />
            </AdminRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/metadata"
        element={
          <ProtectedRoute>
            <AdminRoute>
              <AdminMetadataPage />
            </AdminRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/bulk-listing"
        element={
          <ProtectedRoute>
            <AdminRoute>
              <BulkListingPage />
            </AdminRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/settings"
        element={
          <ProtectedRoute>
            <AdminRoute>
              <AdminSettingsPage />
            </AdminRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/coupons"
        element={
          <ProtectedRoute>
            <AdminRoute>
              <AdminCouponsPage />
            </AdminRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/support"
        element={
          <ProtectedRoute>
            <AdminRoute>
              <AdminSupportPage />
            </AdminRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/revenue"
        element={
          <ProtectedRoute>
            <AdminRoute>
              <AdminRevenuePage />
            </AdminRoute>
          </ProtectedRoute>
        }
      />
      {/* Reached from the Recent transactions card on the revenue screen. */}
      <Route
        path="/admin/transactions"
        element={
          <ProtectedRoute>
            <AdminRoute>
              <AdminTransactionsPage />
            </AdminRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/vendor"
        element={
          <ProtectedRoute>
            <VendorRoute>
              <VendorDashboardPage />
            </VendorRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/vendor/products"
        element={
          <ProtectedRoute>
            <VendorRoute>
              <VendorProductsPage />
            </VendorRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/vendor/reports"
        element={
          <ProtectedRoute>
            <VendorRoute>
              <VendorReportsPage />
            </VendorRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/vendor/products/new"
        element={
          <ProtectedRoute>
            <VendorRoute>
              <VendorProductFormPage />
            </VendorRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/vendor/products/:id/edit"
        element={
          <ProtectedRoute>
            <VendorRoute>
              <VendorProductFormPage />
            </VendorRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/vendor/bulk-listing"
        element={
          <ProtectedRoute>
            <VendorRoute>
              <BulkListingPage />
            </VendorRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/vendor/profile"
        element={
          <ProtectedRoute>
            <VendorRoute>
              <VendorProfilePage />
            </VendorRoute>
          </ProtectedRoute>
        }
      />
      <Route path="/" element={<RootRedirect />} />
    </Routes>
    </Suspense>
  );
};

/**
 * Shown while a page's chunk is downloading.
 *
 * A full-height block rather than a small spinner: an element that is empty
 * and then fills is a layout shift, and layout shift is the thing being
 * avoided. Holding the space means the page below never jumps.
 */
function RouteLoading() {
  return (
    <div className="min-h-screen bg-canvas flex items-center justify-center" aria-busy="true">
      <span className="sr-only">Loading</span>
      <span className="w-7 h-7 rounded-full border-2 border-line border-t-brand-500 animate-spin" />
    </div>
  );
}

const App = () => {
  return (
    <ThemeProvider>
      <AuthProvider>
        <PlatformSettingsProvider>
          {/* Inside AuthProvider: the balance belongs to the signed-in
              account, so it must not be fetched before there is one. */}
          <CreditsProvider>
            {/* Inside AuthProvider: the count belongs to the signed-in account. */}
            <SupportUnreadProvider>
            <ComplianceProvider>
            <BrowserRouter>
              {/* Must be INSIDE the router — it reads the current location.
                  Renders nothing; it only resets scroll on navigation. */}
              <ScrollToTop />
              <AppRoutes />
              {/* Site-wide, not only inside the account: a cookie disclaimer
                  that appears after signing in has already missed the point. */}
              <CookieNotice />
            </BrowserRouter>
            </ComplianceProvider>
            </SupportUnreadProvider>
          </CreditsProvider>
        </PlatformSettingsProvider>
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;
